"use client";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../cn";
import { Button } from "./button";
import { FOCUS_RING } from "./focus-ring";
import { HOVER_EASE } from "./states";

// Design spec §2 detail drawer and §6.7: fixed to the right edge from under the top bar
// (`--shell-top`, the shell's measured top, else the 56 px `--topbar-h`) to the bottom,
// min(960, 100vw − 64) wide (full width below 768), --card with a left hairline and
// --shadow-lg, 220 ms / 16 px slide. Focus is trapped, Escape closes and focus returns to the
// opener. The scrim (and with it a modal page and aria-modal) appears only below 1200 px unless
// `scrim` says otherwise, so on wide screens the list stays clickable and a second click swaps
// the subject.
//
// Two forms, one component:
// - Template (spec §8.2): `<Drawer open onOpenChange>` with the header and body as direct
//   children; they are placed in the panel for you.
// - Composed: `<Drawer>` with `DrawerTrigger` / `DrawerContent` / `DrawerClose` as direct
//   children, controlled or not (`defaultOpen`), like the other Base UI roots.

const WIDE = "(min-width: 1200px)";

type Scrim = "auto" | "always" | "never";

function useScrim(scrim: Scrim): boolean {
  const [wide, setWide] = React.useState(
    () => typeof window !== "undefined" && window.matchMedia?.(WIDE).matches === true,
  );
  React.useEffect(() => {
    if (scrim !== "auto") return;
    const mql = window.matchMedia?.(WIDE);
    if (!mql) return;
    const sync = () => setWide(mql.matches);
    sync();
    mql.addEventListener("change", sync);
    return () => mql.removeEventListener("change", sync);
  }, [scrim]);
  return scrim === "always" || (scrim === "auto" && !wide);
}

type DrawerContextValue = {
  scrimmed: boolean;
  width?: string | undefined;
  className?: string | undefined;
};

const DrawerContext = React.createContext<DrawerContextValue>({ scrimmed: true });

export type DrawerProps = Omit<
  DialogPrimitive.Root.Props,
  "modal" | "open" | "onOpenChange" | "children"
> & {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** CSS width, e.g. "720px"; defaults to --drawer-w. */
  width?: string;
  scrim?: Scrim;
  children: React.ReactNode;
  /** Classes for the panel. */
  className?: string;
};

export function Drawer({
  open,
  onOpenChange,
  width,
  scrim = "auto",
  disablePointerDismissal,
  children,
  className,
  ...props
}: DrawerProps) {
  const scrimmed = useScrim(scrim);
  const composed = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && COMPOUND_PARTS.has(child.type),
  );
  return (
    <DrawerContext.Provider value={{ scrimmed, width, className }}>
      <DialogPrimitive.Root
        data-slot="drawer"
        open={open}
        onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined}
        // Scrimmed: a true modal. Otherwise focus is still trapped, but the page stays visible,
        // clickable and not hidden from assistive technology.
        modal={scrimmed ? true : "trap-focus"}
        disablePointerDismissal={disablePointerDismissal ?? !scrimmed}
        {...props}
      >
        {composed ? children : <DrawerContent>{children}</DrawerContent>}
      </DialogPrimitive.Root>
    </DrawerContext.Provider>
  );
}

export function DrawerTrigger(props: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

export function DrawerClose(props: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="drawer-close" {...props} />;
}

export type DrawerContentProps = Omit<DialogPrimitive.Popup.Props, "style"> & {
  style?: React.CSSProperties;
  /** CSS width, e.g. "720px"; defaults to the Drawer's `width`, then --drawer-w. */
  width?: string;
};

/** The panel itself (portal, scrim when scrimmed, popup). */
export function DrawerContent({ className, width, style, children, ...props }: DrawerContentProps) {
  const ctx = React.useContext(DrawerContext);
  const w = width ?? ctx.width;
  return (
    <DialogPrimitive.Portal data-slot="drawer-portal">
      {ctx.scrimmed ? (
        <DialogPrimitive.Backdrop
          data-slot="drawer-overlay"
          className="fixed inset-x-0 top-(--shell-top,var(--topbar-h)) bottom-0 z-drawer bg-overlay transition-opacity duration-(--dur-slow) data-ending-style:opacity-0 data-starting-style:opacity-0"
        />
      ) : null}
      <DialogPrimitive.Popup
        data-slot="drawer-content"
        aria-modal={ctx.scrimmed || undefined}
        style={w ? ({ ...style, "--drawer-w": w } as React.CSSProperties) : style}
        className={cn(
          "fixed top-(--shell-top,var(--topbar-h)) right-0 bottom-0 z-drawer flex w-(--drawer-w) flex-col border-l border-border bg-card text-sm text-card-foreground shadow-lg outline-none transition-[opacity,translate] duration-(--dur-slow) ease-out data-ending-style:translate-x-4 data-ending-style:opacity-0 data-starting-style:translate-x-4 data-starting-style:opacity-0 max-md:w-full max-md:border-l-0",
          ctx.className,
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Popup>
    </DialogPrimitive.Portal>
  );
}

/** Direct children of `Drawer` that mean "composed": the caller places the panel itself. */
const COMPOUND_PARTS = new Set<unknown>([DrawerTrigger, DrawerContent, DrawerClose]);

export type DrawerHeaderProps = React.ComponentProps<"div"> & {
  /** `ml-auto` buttons on the right. */
  actions?: React.ReactNode;
  closeLabel?: string;
};

/** 56 px: close button, then a 12 px breadcrumb (children), then actions on the right. */
export function DrawerHeader({
  actions,
  closeLabel = "Close",
  className,
  children,
  ...props
}: DrawerHeaderProps) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        "flex h-14 shrink-0 items-center gap-3 border-b border-subtle px-6 max-md:px-4",
        className,
      )}
      {...props}
    >
      <DialogPrimitive.Close
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            className="pointer-coarse:size-11"
            aria-label={closeLabel}
          />
        }
      >
        <XIcon aria-hidden="true" />
      </DialogPrimitive.Close>
      <div className="min-w-0 truncate text-xs text-muted-foreground">{children}</div>
      {actions ? <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** The subject's title: 20 / 600 / −0.4. */
export function DrawerTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="drawer-title"
      className={cn(
        "font-heading text-[20px] leading-7 font-semibold tracking-heading text-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** Scrolling body, padding 20 24. */
export function DrawerBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-body"
      className={cn(
        "min-h-0 flex-1 overflow-y-auto px-6 py-5 scrollbar-gutter-stable max-md:px-4",
        className,
      )}
      {...props}
    />
  );
}

export type DrawerSectionProps = Omit<React.ComponentProps<"section">, "title"> & {
  title: React.ReactNode;
  /** Optional control on the title's line, right-aligned (a small ghost button, a count). */
  action?: React.ReactNode;
};

/** A titled block inside the body: h2 15 / 600, an optional action on the same line. */
export function DrawerSection({
  title,
  action,
  className,
  children,
  ...props
}: DrawerSectionProps) {
  const id = React.useId();
  return (
    <section
      data-slot="drawer-section"
      aria-labelledby={id}
      className={cn("mt-6 first:mt-0", className)}
      {...props}
    >
      <div className="mb-2 flex min-h-7 items-center justify-between gap-2">
        <h2 id={id} className="text-[15px] leading-[22px] font-semibold text-foreground">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** The property chip row under the title (status, owner, due): 28 px, 13 px, hover --muted. */
export function PropertyChip({ className, ...props }: React.ComponentProps<"button">) {
  return (
    <button
      type="button"
      data-slot="property-chip"
      className={cn(
        "inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-sm text-secondary hover:bg-muted hover:text-foreground active:bg-active [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:text-faint",
        HOVER_EASE,
        FOCUS_RING,
        className,
      )}
      {...props}
    />
  );
}
